<template>
  <div class="china-map-wrapper">
    <div v-if="mapBreadList.length > 1" class="toolbar">
      <div class="breadcrumb">
        <span
          v-for="(item, index) in mapBreadList"
          :key="item.code"
          class="crumb"
          @click="jumpToLevel(item, index)"
        >
          {{ item.name }}
          <span v-if="index < mapBreadList.length - 1"> &gt; </span>
        </span>
      </div>
    </div>

    <div class="map-container" ref="chartRef"></div>

    <!-- 告警弹窗 -->
    <div v-if="showAlertPopup" class="alert-popup" :style="popupStyle">
      <div class="alert-header">
        <div class="alert-header-title"> ⚠️ 注意!!! </div>
        <div class="alert-header-extra">
          <button class="close-btn" @click="closeAlertPopup">×</button>
        </div>
      </div>
      <div class="alert-content">
        检测到
        <strong
          >{{ currentAlert.devName }} <i>{{ currentAlert.clientId }}</i></strong
        >
        发生跌倒，请及时关注！
      </div>
    </div>

    <div
      v-for="(alert, index) in visibleAutoAlerts"
      :key="alert.id"
      class="auto-alert-toast"
      :style="{ top: `${20 + index * 110}px` }"
    >
      <div class="toast-content">
        <span class="toast-icon">⚠️</span>
        <div class="toast-details">
          <div class="toast-title">监测到跌倒事件</div>
          <div class="toast-subtitle">发生设备：{{ alert.devName }} {{ alert.clientId }}</div>
        </div>
        <button class="toast-close" @click="removeAutoAlert(alert.id)">×</button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import * as echarts from 'echarts'
import { ref, onMounted, reactive, onUnmounted, nextTick } from 'vue'
import divisionData from '@/assets/data/pca-code.json'
import * as statsApi from '@/api/stats'
import { usePolling } from '@/hooks/usePolling'
import mqtt, { type MqttClient } from 'mqtt'
import dayjs from 'dayjs'
import centroid from '@turf/centroid'

defineOptions({
  name: 'mapDashboard',
})

interface Division {
  code: string
  name: string
  children?: Division[]
}

interface AlertInfo {
  id: string
  devName: string
  clientId: string
  msgType?: string
  provinceCode?: string
  cityCode?: string
  districtCode?: string
  coordinates?: [number, number]
}

interface GeoJSONFeature {
  type: string
  properties: {
    name: string
    adcode?: string
    id?: string
    parent: { adcode: number }
    [key: string]: unknown
  }
  geometry: {
    coordinates: number[]
    type: string
  }
}

interface GeoJSON {
  type: string
  features: GeoJSONFeature[]
  geometry: {
    type: string
  }
}

interface EChartsEventParams {
  componentType: string
  seriesType: string
  seriesIndex: number
  seriesName: string
  name: string
  dataIndex: number
  data: AlertInfo & { value: [number, number] }
  event?: {
    event: MouseEvent
    [key: string]: unknown
  }
  [key: string]: unknown
}

interface MapEventParams {
  name: string
  [key: string]: unknown
}

interface AreaData {
  onlineCount: number
  tenantUserCount: number
  userCount: number
  monthFallCount: number
}

// 响应式数据
const chartRef = ref<HTMLDivElement>()
let chart: echarts.ECharts | null = null

const divisions = ref<Division[]>([])
const currentAdcode = ref('100000') // 340100  100000
// const currentLevel = ref(0)
const areaDataCache = ref<Record<string, AreaData>>({})
const currentGeoJson = ref<GeoJSON | null>(null)

// 地图面包屑导航
const mapBreadList = ref<{ name: string; code: number }[]>([{ name: '全国', code: 100000 }])

// 告警相关状态
const showAlertPopup = ref(false)
const currentAlert = reactive({
  devName: '',
  clientId: '',
})
const popupStyle = ref({
  left: '0px',
  top: '0px',
})

const visibleAutoAlerts = ref<AlertInfo[]>([])
const autoAlertTimers = ref<Record<string, NodeJS.Timeout>>({})

// 动态告警数据（从MQTT接收）
const alertData = ref<AlertInfo[]>([])

// type Coordinate = [number, number]

// async function getDistrictCenter(code: string, districtCode: string): Promise<Coordinate> {
//   const geoJson = await loadGeoJSON(`${code}00`)
//   console.log('getDistrictCenter', code, districtCode, geoJson)

//   if (!geoJson || !geoJson.features) return [0, 0]

//   const feature = geoJson.features.find((f: GeoJSONFeature) => f.properties.adcode == districtCode)
//   console.log('get feature ', feature?.properties.name, feature)

//   if (!feature || !feature.geometry || !feature.geometry.coordinates) return [0, 0]

//   const coords = feature.geometry.coordinates

//   const allPoints: Coordinate[] = []

//   coords.forEach((polygon: Coordinate[][]) => {
//     polygon.forEach((ring: Coordinate[]) => {
//       ring.forEach((point: Coordinate) => {
//         allPoints.push(point)
//       })
//     })
//   })
//   const center = centroid(feature).geometry.coordinates
//   return center as Coordinate
// }

// ========== 轮询和MQTT集成 ==========
let mqttClient: MqttClient | null = null
const subscribeAlartData = async () => {
  const mqttConfig = {
    host: import.meta.env.VITE_MQTT_HOST_POINT,
    username: import.meta.env.VITE_MQTT_USERNAME,
    password: import.meta.env.VITE_MQTT_PASSWORD,
    clientId: `web_mqtt_data${Math.random().toString(16).slice(2)}`,
  }
  mqttClient = mqtt.connect(mqttConfig.host, {
    clientId: mqttConfig.clientId,
    username: mqttConfig.username,
    password: mqttConfig.password,
  })
  console.log(dayjs().format('YYYY-MM-DD HH:mm:ss'), '⌛️ mqttClient connect ready', mqttClient)
  mqttClient.on('connect', () => {
    console.log(dayjs().format('YYYY-MM-DD HH:mm:ss'), '✅ MQTT已连接')
    const sub = `/mps/admin/notice`
    mqttClient?.subscribe(sub, (err) => {
      if (err) {
        console.error(dayjs().format('YYYY-MM-DD HH:mm:ss'), '❌ MQTT订阅失败', err)
      } else {
        console.log(dayjs().format('YYYY-MM-DD HH:mm:ss'), `⚛️ 已订阅主题 ${sub}`)
      }
    })
  })
  mqttClient.on('error', (err) => {
    console.error('❌ MQTT连接错误', err)
  })
  mqttClient.on('message', async (topic: string, message: Uint8Array) => {
    console.log('mqtt message', topic, message.toString())
    const msg = JSON.parse(message.toString())
    const newAlert: AlertInfo = {
      id: msg.eventListId,
      devName: msg.devName,
      clientId: msg.clientId,
      msgType: msg.msgType,
      provinceCode: msg.provinceCode,
      cityCode: msg.cityCode,
      districtCode: msg.districtCode,
      // coordinates: (await getDistrictCenter(msg.cityCode, msg.districtCode)) || undefined,
      coordinates: undefined,
    }
    alertData.value.push(newAlert)

    console.log(
      '新增告警数据',
      newAlert,
      alertData.value,
      currentGeoJson.value,
      11,
      msg.districtCode,
      currentGeoJson.value?.features.find((f) => f.properties.adcode == msg.districtCode)
    )
    // 更新地图上的告警点
    // if (
    //   currentGeoJson.value &&
    //   currentGeoJson.value.features.some((f) => f.properties.adcode == newAlert.districtCode)
    // ) {
    //   showMultipleAutoAlerts([newAlert])
    //   updateAlertSeries()
    // }
    showMultipleAutoAlerts([newAlert])
    updateAlertSeries()
  })

  // ====================mock=====================
  // const str =
  //   '{"clientId":"94A9900B0B53","event":"fall_confirmed","msgType":"fall","devName":"唐家韩的第一台设备","eventListId":"1986248816475205634","provinceCode":"34","cityCode":"3401","districtCode":"340104"}'

  // const msg = JSON.parse(str)

  // const newAlert: AlertInfo = {
  //   id: msg.eventListId,
  //   devName: msg.devName,
  //   clientId: msg.clientId,
  //   msgType: msg.msgType,
  //   provinceCode: msg.provinceCode,
  //   cityCode: msg.cityCode,
  //   districtCode: msg.districtCode,
  //   coordinates: (await getDistrictCenter(msg.cityCode, msg.districtCode)) || undefined,
  // }
  // alertData.value.push(newAlert)

  // if (
  //   currentGeoJson.value &&
  //   currentGeoJson.value.features.some((f) => f.properties.id === newAlert.districtCode)
  // ) {
  //   showMultipleAutoAlerts([newAlert])
  //   updateAlertSeries()
  // }
}

/**
 * 更新告警系列数据
 */
function updateAlertSeries(): void {
  if (!chart || !currentGeoJson.value) return

  const currentAlerts = getCurrentAlerts(currentGeoJson.value)

  chart.setOption({
    series: [{}, getAlertSeries(currentAlerts)],
  })
  chart.resize()
  chart.dispatchAction({
    type: 'geoRoam',
    // name: adcode,
    zoom: 1.2,
    center: null,
  })
}

/**
 * 轮询获取区域数据
 */
const pollAreaData = async (): Promise<void> => {
  if (!currentGeoJson.value) return

  try {
    await fetchAreaDataBatch(currentGeoJson.value)
    updateMapData()
  } catch (error) {
    console.error('轮询获取区域数据失败:', error)
  }
}

/**
 * 更新地图数据
 */
function updateMapData(): void {
  if (!chart || !currentGeoJson.value) return

  chart.setOption({
    series: [getMapSeries(currentAdcode.value, currentGeoJson.value)],
  })
  chart.resize()
  chart.dispatchAction({
    type: 'geoRoam',
    // name: adcode,
    zoom: 1.2,
    center: null,
  })
}

const { start: startPolling, stop: stopPolling } = usePolling(pollAreaData, 10000)

// ========== 1. 地图基础渲染 ==========
/**
 * 加载地图GeoJSON数据
 */
async function loadGeoJSON(adcode: string): Promise<GeoJSON> {
  // const url = `https://hflnxx.oss-cn-shanghai.aliyuncs.com/MAP/20251105/${adcode}.json`
  // const url = `https://geo.datav.aliyun.com/areas/bound/${adcode}_full.json`
  const url = `/geo-boundary/areas/bound/${adcode}_full.json`
  const res = await fetch(url, {
    headers: {
      Accept: 'application/json',
    },
  })
  return (await res.json()) as GeoJSON
}

/**
 * 初始化ECharts实例
 */
function initChart(): void {
  if (!chart && chartRef.value) {
    chart = echarts.init(chartRef.value)
  }
}

/**
 * 获取地图系列配置
 */
function getMapSeries(adcode: string, geoJson: GeoJSON): echarts.MapSeriesOption {
  return {
    type: 'map',
    map: adcode,
    geoIndex: 0,
    data: geoJson.features.map((f: GeoJSONFeature) => {
      const id = f.properties.adcode?.toString()
      const areaData = id ? areaDataCache.value[id] : undefined

      return {
        name: f.properties.name,
        value: areaData?.userCount,
        areaData: areaData,
      }
    }),
    itemStyle: {
      borderColor: '#2076ed',
      borderWidth: 1,
      areaColor: '#122d9a',
      shadowColor: 'rgba(32, 118, 237, 0.5)',
      shadowBlur: 10,
    },
    emphasis: {
      itemStyle: {
        areaColor: '#2076ed',
        borderColor: '#a4f6fa',
      },
    },
  }
}

/**
 * 获取告警点系列配置
 */
function getAlertSeries(currentAlerts: AlertInfo[]): echarts.ScatterSeriesOption {
  return {
    type: 'scatter',
    coordinateSystem: 'geo',
    tooltip: {
      show: false,
    },
    symbol: getAlertSymbol,
    symbolSize: 25,
    itemStyle: {
      color: 'red',
      shadowBlur: 10,
      shadowColor: '#000',
    },
    data: currentAlerts.map((alert) => ({
      ...alert,
      value: alert.coordinates,
      symbol: getAlertSymbol(),
      itemStyle: {
        color: '#ff4d4f',
      },
      id: alert.id,
      name: alert.devName,
      type: alert.msgType,
    })),
    emphasis: {
      itemStyle: {
        shadowBlur: 20,
        shadowColor: '#ff4d4f',
      },
    },
    select: {
      itemStyle: {
        borderColor: '#fff',
        borderWidth: 2,
      },
    },
  }
}

/**
 * 设置地图事件监听
 */
function setupMapEvents(geoJson: GeoJSON): void {
  if (!chart) return

  // 移除旧的事件监听器
  chart.off('click')

  // 添加地图区域点击事件
  chart.on('click', (params: EChartsEventParams | MapEventParams) => {
    console.log('点击事件参数:', params)

    if (
      'componentType' in params &&
      params.componentType === 'series' &&
      params.seriesType === 'scatter'
    ) {
      // 点击告警点 - 显示详细弹窗
      console.log('点击告警点:', params.data)

      // 直接从告警数据中查找对应的告警信息
      const alert = alertData.value.find((a) => a.id === params.data.id)
      if (alert && params.event?.event) {
        console.log('找到告警信息:', alert) // 添加日志调试
        showAlertDetail(alert, params.event.event)
      } else {
        console.warn('未找到对应的告警信息:', params.data.id)
      }
    } else {
      // 点击地图区域
      const clicked = geoJson.features.find(
        (f: GeoJSONFeature) => f.properties.name === params.name
      )
      const adCode = clicked?.properties.adcode?.toString() ?? ''
      currentAdcode.value = adCode

      if (mapBreadList.value.length < 3) {
        mapBreadList.value.push({
          name: clicked?.properties.name ?? '',
          code: Number(adCode),
        })
      }

      renderMap(adCode)
    }
  })
}

/**
 * 主渲染函数
 */
async function renderMap(adcode: string): Promise<void> {
  // 1. 加载地图数据
  const geoJson = await loadGeoJSON(adcode)
  currentGeoJson.value = geoJson
  echarts.registerMap(adcode, geoJson)

  // 2. 初始化图表
  initChart()
  if (!chart) return

  // 3. 批量获取区域数据
  await fetchAreaDataBatch(geoJson)

  // 4. 获取当前区域的告警数据
  const currentAlerts = getCurrentAlerts(geoJson)

  // 5. 显示自动告警提示
  if (currentAlerts.length > 0) {
    showMultipleAutoAlerts(currentAlerts)
  }

  // 6. 设置ECharts配置
  chart.setOption({
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'item',
      backgroundColor: 'rgba(18, 45, 154, 0.8)',
      borderColor: '#2076ed',
      textStyle: {
        color: '#a4f6fa',
      },
      formatter: (params: EChartsEventParams | MapEventParams) => formatTooltip(params),
    },
    visualMap: {
      show: false,
      left: '10px',
      bottom: '80px',
      min: Math.min(...Object.values(areaDataCache.value).map((d) => d.userCount || 0)),
      max: Math.max(...Object.values(areaDataCache.value).map((d) => d.userCount || 0)),
      text: ['高', '低'],
      textStyle: {
        color: '#a4f6fa',
      },
      realtime: true,
      calculable: true,
      inRange: {
        color: ['#0b3c5d', '#0d4c73', '#0f5b8a', '#116ba0', '#328cc1'],
      },
    },
    geo: {
      map: adcode,
      roam: true,
      zoom: 1.2,
      label: {
        show: true,
        color: '#fff',
        fontSize: 0,
        fontWeight: 600,
        fontStyle: 'italic',
      },
      itemStyle: {
        borderColor: '#00ffff',
        borderWidth: 1,
        areaColor: '#122d9a',
      },
      emphasis: {
        label: {
          show: true,
          color: '#fff',
          fontSize: 10,
        },
        itemStyle: {
          areaColor: '#2076ed',
          borderColor: '#fff',
          borderWidth: 2,
        },
      },
    },
    series: [getMapSeries(adcode, geoJson), getAlertSeries(currentAlerts)],
  })

  chart.resize()
  chart.dispatchAction({
    type: 'geoRoam',
    name: adcode,
    zoom: 1.2,
    center: null,
  })

  // 7. 设置事件监听
  setupMapEvents(geoJson)
  updateBreadcrumb(adcode, geoJson)

  nextTick(() => {
    if (adcode === '100000') {
      flashHighlight('安徽省', { infinite: true })
    } else if (adcode === '340000') {
      flashHighlight('合肥市', { infinite: true })
    }
  })
}

// ========== 2. 批量获取区域数据 ==========
/**
 * 批量获取区域数据
 */
const fetchAreaDataBatch = async (geoJson: GeoJSON): Promise<void> => {
  try {
    const areaAdcodes: { adcode: string; level: string }[] = geoJson.features
      .map((f) => ({
        adcode: f.properties.adcode?.toString() ?? '',
        level: f.properties.level?.toString() ?? '',
      }))
      .filter((area) => area.level !== '')

    const promises = areaAdcodes.map((area) => {
      const codeValue =
        area.level === 'province'
          ? area.adcode.slice(0, 2)
          : area.level === 'city'
            ? area.adcode.slice(0, 4)
            : area.adcode
      return statsApi
        .statsV2MapDisplay({
          code: codeValue,
          codeType: area.level as 'province' | 'city' | 'district',
        })
        .then((response) => {
          return { area, data: response.data }
        })
        .catch(() => {
          return { area, data: null }
        })
    })

    const results = await Promise.all(promises)

    results.forEach(({ area, data }) => {
      if (area?.adcode && data) {
        areaDataCache.value[area.adcode] = {
          onlineCount: data.onlineCount || 0,
          tenantUserCount: data.tenantUserCount || 0,
          userCount: data.userCount || 0,
          monthFallCount: data.monthFallCount || 0,
        }
      }
    })
  } catch (error) {
    console.error('批量获取区域数据失败:', error)
  }
}

// ========== 3. 告警数据处理 ==========
/**
 * 获取当前地图区域的告警数据
 */
function getCurrentAlerts(geoJson: GeoJSON): AlertInfo[] {
  return alertData.value.filter((alert) =>
    geoJson.features.some((f: GeoJSONFeature) => f.properties.adcode == alert.districtCode)
  )
}

/**
 * 获取告警点样式
 */
function getAlertSymbol(): string {
  return 'path://M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z'
}

/**
 * 显示多条自动告警提示
 */
function showMultipleAutoAlerts(alerts: AlertInfo[]): void {
  // 限制最多显示5条
  const alertsToShow = alerts.slice(0, 5)

  // 清除之前的定时器
  Object.values(autoAlertTimers.value).forEach((timer) => {
    if (timer) clearTimeout(timer)
  })
  autoAlertTimers.value = {}

  // 清空当前显示的告警
  visibleAutoAlerts.value = []

  // 依次添加告警，每条间隔1秒显示，避免同时弹出
  alertsToShow.forEach((alert, index) => {
    setTimeout(() => {
      // 如果已经达到最大数量，移除最早的一条
      if (visibleAutoAlerts.value.length >= 5) {
        const removedAlert = visibleAutoAlerts.value.shift()
        if (removedAlert && autoAlertTimers.value[removedAlert.id]) {
          clearTimeout(autoAlertTimers.value[removedAlert.id])
          delete autoAlertTimers.value[removedAlert.id]
        }
      }

      // 添加新告警
      visibleAutoAlerts.value.push(alert)

      // 设置x秒后自动移除
      autoAlertTimers.value[alert.id] = setTimeout(() => {
        removeAutoAlert(alert.id)
      }, 5000)
    }, index * 1000)
  })
}

/**
 * 移除单个自动告警
 */
function removeAutoAlert(alertId: string): void {
  const index = visibleAutoAlerts.value.findIndex((alert) => alert.id === alertId)
  if (index !== -1) {
    visibleAutoAlerts.value.splice(index, 1)
  }

  // 清除对应的定时器
  if (autoAlertTimers.value[alertId]) {
    clearTimeout(autoAlertTimers.value[alertId])
    delete autoAlertTimers.value[alertId]
  }
}

// ========== 工具函数 ==========
/**
 * Tooltip 格式化函数
 */
function formatTooltip(params: EChartsEventParams | MapEventParams): string {
  if (
    'componentType' in params &&
    params.componentType === 'series' &&
    params.seriesType === 'scatter'
  ) {
    return ''
  } else {
    const name = 'name' in params ? params.name : ''
    const areaData =
      'data' in params && params.data
        ? (params.data as { areaData?: AreaData }).areaData
        : undefined

    const deviceOnline = areaData?.onlineCount || 0
    const organizationCount = areaData?.tenantUserCount || 0
    const userCount = areaData?.userCount || 0
    const monthlyAlert = areaData?.monthFallCount || 0

    return `
      <div style="padding: 5px;">
        <div style="font-size: 14px; font-weight: bold; margin-bottom: 8px; color: #fff;">${name}</div>
        <div style="display: flex; flex-direction: column; gap: 4px;">
          <div style="display: flex; justify-content: space-between;">
            <span>设备在线：</span>
            <span style="color: #4ae86e; font-weight: bold;">${deviceOnline}台</span>
          </div>
          <div style="display: flex; justify-content: space-between;">
            <span>机构数量：</span>
            <span style="color: #4ae86e; font-weight: bold;">${organizationCount}个</span>
          </div>
          <div style="display: flex; justify-content: space-between;">
            <span>服务用户：</span>
            <span style="color: #4ae86e; font-weight: bold;">${userCount}个</span>
          </div>
          <div style="display: flex; justify-content: space-between;">
            <span>月度事件告警：</span>
            <span style="color: #ff6b6b; font-weight: bold;">${monthlyAlert}个</span>
          </div>
        </div>
      </div>
    `
  }
}

/**
 * 显示告警详情弹窗
 */
function showAlertDetail(alert: AlertInfo, event: MouseEvent): void {
  console.log('显示告警详情:', alert)
  Object.assign(currentAlert, alert)

  // 设置弹窗位置，确保在可视区域内
  const popupWidth = 400 // 弹窗宽度
  const popupHeight = 300 // 弹窗高度
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  let left = event.clientX + 10
  let top = event.clientY + 10

  // 如果弹窗会超出右边界，调整位置
  if (left + popupWidth > viewportWidth) {
    left = event.clientX - popupWidth - 10
  }

  // 如果弹窗会超出下边界，调整位置
  if (top + popupHeight > viewportHeight) {
    top = event.clientY - popupHeight - 10
  }

  popupStyle.value = {
    left: `${Math.max(10, left)}px`, // 确保不会超出左边界
    top: `${Math.max(10, top)}px`, // 确保不会超出上边界
  }

  showAlertPopup.value = true

  // 添加点击外部关闭的功能
  setTimeout(() => {
    document.addEventListener('click', closeAlertOnClickOutside)
  }, 100)
}

function closeAlertOnClickOutside(event: MouseEvent): void {
  const alertPopup = document.querySelector('.alert-popup') as HTMLElement
  if (alertPopup && !alertPopup.contains(event.target as Node)) {
    closeAlertPopup()
  }
}

/**
 * 关闭告警弹窗
 */
function closeAlertPopup(): void {
  showAlertPopup.value = false
  document.removeEventListener('click', closeAlertOnClickOutside)
}

// MQTT连接
let mqttInterval: number | undefined
function startMqttSimulation() {
  subscribeAlartData()
}

function stopMqttSimulation() {
  if (mqttInterval) {
    clearInterval(mqttInterval)
    mqttInterval = undefined
  }
}

onMounted(() => {
  divisions.value = divisionData

  const observer = new ResizeObserver(() => {
    if (chartRef.value?.clientWidth && chartRef.value?.clientHeight) {
      renderMap(currentAdcode.value)
      observer.disconnect()
    }
  })

  if (chartRef.value) observer.observe(chartRef.value)

  // 启动轮询和MQTT
  startPolling()
  startMqttSimulation()
})

onUnmounted(() => {
  stopPolling()
  stopMqttSimulation()
})

// ========== 新增函数：更新面包屑 ==========
function updateBreadcrumb(adcode: string, geoJson: GeoJSON | null): void {
  // 如果全国则重置
  if (adcode === '100000') {
    mapBreadList.value = [{ name: '全国', code: 100000 }]
    return
  }

  // 尝试从 geoJson 中获取当前层的显示名称
  const name = geoJson?.features?.[0]?.properties?.name ?? `区域 ${adcode}`
  const code = Number(adcode)

  // 已存在则不重复 push（点击面包屑或下钻时会已存在/已 push 的情况）
  const exists = mapBreadList.value.some((b) => b.code === code)
  if (!exists) {
    mapBreadList.value.push({ name, code })
  }
}

/**
 * 点面包屑跳转到某一级（新增）
 */
function jumpToLevel(item: { name: string; code: number }, index: number): void {
  // 截断面包屑
  mapBreadList.value = mapBreadList.value.slice(0, index + 1)
  currentAdcode.value = String(item.code)
  renderMap(String(item.code))
}

/**
 * 高亮闪烁引导函数
 * @param name 区域名称（与 geoJson feature.properties.name 完全匹配）
 * @param options 控制参数
 *   - seriesIndex: 地图序号，默认 0
 *   - times: 闪烁次数（默认 6）
 *   - infinite: 是否无限闪烁（true 则忽略 times）
 *   - interval: 每次闪烁的间隔（ms，默认 600）
 */
function flashHighlight(
  name: string,
  options: { seriesIndex?: number; times?: number; infinite?: boolean; interval?: number } = {}
) {
  const chartInstance = chart
  if (!chartInstance) return

  const seriesIndex = options.seriesIndex ?? 0
  const times = options.times ?? 6
  const infinite = options.infinite ?? false
  const interval = options.interval ?? 600

  let count = 0
  let visible = false

  const toggle = () => {
    visible = !visible
    try {
      chartInstance.dispatchAction({
        type: visible ? 'highlight' : 'downplay',
        seriesIndex,
        name,
      })
    } catch (e) {
      console.warn('[flashHighlight] dispatchAction error:', e)
    }
  }

  toggle() // 立即执行一次，先亮起

  const timer = setInterval(() => {
    toggle()
    count++
    if (!infinite && count >= times) {
      clearInterval(timer)
      // 结束时保持高亮状态
      chartInstance.dispatchAction({ type: 'highlight', seriesIndex, name })
    }
  }, interval)
}
</script>

<style lang="less" scoped>
.china-map-wrapper {
  width: 100%;
  height: 100%;
  position: relative;

  .toolbar {
    position: absolute;
    top: 16px;
    left: 16px;
    z-index: 10;
    display: flex;
    flex-direction: column;
    gap: 8px;

    .breadcrumb {
      margin-top: 4px;
      font-size: 14px;
      font-weight: 600;
      color: #a4f6fa;
      display: flex;
      flex-wrap: wrap;
      gap: 6px;

      .crumb {
        cursor: pointer;
        color: #fff;
        &:hover {
          text-decoration: underline;
          color: #a4f6fa;
        }
      }
    }
  }

  .map-container {
    width: 100%;
    height: 100%;
    min-height: 400px;
  }

  .alert-popup {
    position: fixed;
    z-index: 1000;
    background: rgba(255, 107, 107, 0.3);
    border: 2px solid #ff6b6b;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(255, 107, 107, 0.3);
    width: 350px;
    backdrop-filter: blur(10px);

    .alert-header {
      display: flex;
      align-items: center;
      padding: 10px 12px;
      background: rgba(255, 107, 107, 0.5);
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      display: flex;
      justify-content: space-between;

      .alert-header-title {
        font-size: 16px;
        font-weight: bold;
        color: #fff;
        flex: 1;
      }
      .alert-header-extra {
        font-size: 14px;
        color: #fff;
      }

      .close-btn {
        background: none;
        border: none;
        color: #fff;
        font-size: 20px;
        cursor: pointer;
        padding: 0;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;

        &:hover {
          background: rgba(255, 255, 255, 0.1);
        }
      }
    }

    .alert-content {
      padding: 12px;
      line-height: 1.4;
    }
  }

  .auto-alert-toast {
    position: fixed;
    right: 20px;
    z-index: 1001;
    background: linear-gradient(135deg, #ff8383 0%, #fc5555 100%);
    border-radius: 8px;
    padding: 16px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    width: 350px;
    animation: slideIn 0.3s ease-out;

    .toast-content {
      display: flex;
      align-items: flex-start;
      gap: 12px;

      .toast-icon {
        font-size: 20px;
        flex-shrink: 0;
        margin-top: 2px;
      }

      .toast-details {
        flex: 1;

        .toast-title {
          color: #fff;
          font-weight: bold;
          font-size: 16px;
          margin-bottom: 4px;
        }

        .toast-subtitle {
          color: #fff;
          line-height: 1.4;
          font-size: 14px;
        }
      }

      .toast-close {
        background: none;
        border: none;
        color: #fff;
        font-size: 18px;
        cursor: pointer;
        padding: 0;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        &:hover {
          background: rgba(255, 255, 255, 0.1);
        }
      }
    }

    &.alert-level-high {
      border-left: 4px solid #ff4d4f;
    }

    &.alert-level-medium {
      border-left: 4px solid #faad14;
    }

    &.alert-level-low {
      border-left: 4px solid #52c41a;
    }

    &.alert-index-0 {
      animation-delay: 0s;
    }
    &.alert-index-1 {
      animation-delay: 0.1s;
    }
    &.alert-index-2 {
      animation-delay: 0.2s;
    }
    &.alert-index-3 {
      animation-delay: 0.3s;
    }
    &.alert-index-4 {
      animation-delay: 0.4s;
    }
  }
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}
</style>
